/**
 * Palette de recherche globale Ctrl+K (Lot H.5).
 *
 * Composant DÉDIÉ (et non le `Modal` générique) : le pattern palette exige que le
 * focus reste DANS le champ (sélection virtuelle via `aria-activedescendant`),
 * sans focus-trap Tab. A11y (cf 04-ux § Surface 4) :
 *   - conteneur `role="dialog"`, champ `role="combobox"`, liste `role="listbox"`,
 *     lignes `role="option"` ;
 *   - navigation ↑/↓ sur la liste APLATIE (les en-têtes de groupe sont sautés) ;
 *   - `Entrée` ouvre la fiche + ferme, `Échap` ferme (focus rendu au bouton Topbar).
 *
 * La requête serveur (`recherche.global`) est déjà filtrée RLS + permission : on
 * n'affiche QUE ce que l'API renvoie, sans aucun filtrage côté front.
 */

import { type RechercheResultat } from "@champ-erp/contracts";
import { Spinner } from "@champ-erp/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { type KeyboardEvent, type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { tenantQueryKey } from "../../lib/tenant-query-key.js";
import { trpc } from "../../lib/trpc.js";

import { GroupeResultats } from "./GroupeResultats.js";
import { ajouterRecent, lireRecents } from "./recents.js";
import { optionId } from "./ResultatRecherche.js";

const LISTBOX_ID = "command-palette-listbox";
const INPUT_ID = "command-palette-input";
/** Seuil minimal de frappe (aligné sur le contrat `searchGlobalInput`). */
const MIN_CARACTERES = 2;
/** Délai de debounce de la frappe avant requête (cf contrat clavier). */
const DEBOUNCE_MS = 250;

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** Domaine courant : scope local des résultats et des récents. */
  domaineId: string | null;
  /** Bouton déclencheur du Topbar : on lui rend le focus à la fermeture (a11y). */
  triggerRef?: RefObject<HTMLButtonElement | null>;
}

export function CommandPalette({ open, onClose, domaineId, triggerRef }: CommandPaletteProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [requeteDebouncee, setRequeteDebouncee] = useState("");
  const [indexActif, setIndexActif] = useState(0);
  const [recents, setRecents] = useState<RechercheResultat[]>([]);
  // Mémorise l'état ouvert précédent : on ne restaure le focus au trigger QUE
  // lors d'une vraie transition ouvert → fermé (pas au montage initial).
  const etaitOuvert = useRef(false);

  // Réinitialisation à chaque ouverture : recharge les récents, vide la requête,
  // remet la sélection en tête, et met le focus dans le champ (autofocus a11y).
  useEffect(() => {
    if (!open) return;
    setRecents(lireRecents(domaineId));
    setQuery("");
    setRequeteDebouncee("");
    setIndexActif(0);
    // rAF : le champ n'existe dans le DOM qu'après le rendu d'ouverture.
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [open, domaineId]);

  // Restauration du focus au bouton Topbar à la fermeture (contrat clavier §Échap).
  useEffect(() => {
    if (open) {
      etaitOuvert.current = true;
      return;
    }
    if (etaitOuvert.current) {
      etaitOuvert.current = false;
      triggerRef?.current?.focus();
    }
  }, [open, triggerRef]);

  // Debounce ~250ms : on ne lance la requête qu'après une pause de frappe.
  useEffect(() => {
    const trimmee = query.trim();
    const id = setTimeout(() => setRequeteDebouncee(trimmee), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  const rechercheActive = requeteDebouncee.length >= MIN_CARACTERES;

  const { data, isFetching } = useQuery({
    queryKey: tenantQueryKey(domaineId, "recherche.global", requeteDebouncee),
    queryFn: () => trpc.recherche.global.query({ query: requeteDebouncee }),
    enabled: Boolean(domaineId) && open && rechercheActive,
    // Garde les résultats précédents visibles pendant la frappe suivante : évite
    // le clignotement liste → vide → liste à chaque caractère.
    placeholderData: keepPreviousData,
  });

  // Groupes AFFICHÉS : résultats serveur si recherche active, sinon « Récents ».
  const groupes = useMemo(() => {
    if (rechercheActive) {
      return (data?.groupes ?? []).map((g) => ({
        cle: g.type,
        libelle: g.libelle,
        resultats: g.resultats,
      }));
    }
    if (recents.length > 0) {
      return [{ cle: "recents", libelle: "Récents", resultats: recents }];
    }
    return [];
  }, [rechercheActive, data, recents]);

  // Liste APLATIE (sans les en-têtes) : support de la navigation ↑/↓.
  const resultatsAplatis = useMemo(() => groupes.flatMap((g) => g.resultats), [groupes]);

  // Index de départ de chaque groupe dans la liste aplatie → chaque ligne connaît
  // son index global (aria-activedescendant + surlignage).
  const offsets = useMemo(() => {
    const acc: number[] = [];
    let courant = 0;
    for (const groupe of groupes) {
      acc.push(courant);
      courant += groupe.resultats.length;
    }
    return acc;
  }, [groupes]);

  // Nouvelle réponse (ou nouveaux récents) → la sélection repart en tête.
  useEffect(() => {
    setIndexActif(0);
  }, [requeteDebouncee, recents]);

  // Garde la ligne active visible quand on navigue au clavier.
  useEffect(() => {
    if (resultatsAplatis.length === 0) return;
    document.getElementById(optionId(indexActif))?.scrollIntoView({ block: "nearest" });
  }, [indexActif, resultatsAplatis.length]);

  function ouvrirResultat(resultat: RechercheResultat) {
    ajouterRecent(domaineId, resultat);
    onClose();
    navigate(resultat.url_cible);
  }

  // Navigation clavier centralisée sur le champ (le focus NE quitte JAMAIS
  // l'input). ↑/↓ bouclent sur la liste aplatie (les en-têtes ne sont pas dedans).
  function surToucheChamp(evenement: KeyboardEvent<HTMLInputElement>) {
    const total = resultatsAplatis.length;
    if (evenement.key === "ArrowDown") {
      evenement.preventDefault();
      if (total > 0) setIndexActif((i) => (i + 1) % total);
    } else if (evenement.key === "ArrowUp") {
      evenement.preventDefault();
      if (total > 0) setIndexActif((i) => (i - 1 + total) % total);
    } else if (evenement.key === "Enter") {
      evenement.preventDefault();
      const resultat = resultatsAplatis[indexActif];
      if (resultat) ouvrirResultat(resultat);
    } else if (evenement.key === "Escape") {
      evenement.preventDefault();
      onClose();
    }
  }

  if (!open) return null;

  const aucunResultat = rechercheActive && !isFetching && resultatsAplatis.length === 0;
  const chargementInitial = rechercheActive && isFetching && resultatsAplatis.length === 0;

  // Portail vers `document.body` : le `fixed inset-0` est ainsi relatif au VIEWPORT
  // et non à un ancêtre transformé du shell (qui décalait la palette vers la droite).
  return createPortal(
    // Backdrop : un clic HORS de la palette la ferme (le clic DANS est stoppé).
    <div
      className="fixed inset-0 z-[90] flex items-start justify-center bg-black/40 px-4 pt-[12vh]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Recherche globale"
        className="flex max-h-[76vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-champagne-900/10"
        onClick={(evenement) => evenement.stopPropagation()}
      >
        {/* Champ de recherche (role combobox). */}
        <div className="flex items-center gap-3 border-b border-champagne-100 px-4">
          <Search size={20} aria-hidden="true" className="shrink-0 text-champagne-500" />
          <input
            ref={inputRef}
            id={INPUT_ID}
            type="text"
            role="combobox"
            aria-expanded={resultatsAplatis.length > 0}
            aria-controls={LISTBOX_ID}
            aria-activedescendant={resultatsAplatis.length > 0 ? optionId(indexActif) : undefined}
            aria-autocomplete="list"
            aria-label="Rechercher un lot, une cuvée, un client…"
            value={query}
            onChange={(evenement) => setQuery(evenement.target.value)}
            onKeyDown={surToucheChamp}
            placeholder="Rechercher un lot, une cuvée, un client…"
            // Le champ est toujours focalisé quand la palette est ouverte : on
            // supprime le gros contour doré global `:focus-visible` (bruit visuel,
            // « boîte dans la boîte »). Style INLINE car la règle globale
            // `:focus-visible` bat les utilitaires Tailwind — l'inline gagne à coup
            // sûr. La modale signale déjà le contexte actif ; la navigation a11y
            // passe par `aria-activedescendant` sur les résultats.
            style={{ outline: "none" }}
            className="h-14 w-full border-0 bg-transparent text-[16px] text-aubergine-900 placeholder:text-champagne-800/40"
          />
          {rechercheActive && isFetching && (
            <Spinner size="sm" className="shrink-0 text-champagne-500" />
          )}
        </div>

        {/* Zone de résultats (role listbox). */}
        <div
          id={LISTBOX_ID}
          role="listbox"
          aria-label="Résultats de recherche"
          className="min-h-0 flex-1 overflow-y-auto py-1"
        >
          {!rechercheActive && recents.length === 0 ? (
            <p className="px-4 py-8 text-center text-[13px] text-champagne-700">
              Tapez au moins {MIN_CARACTERES} caractères pour rechercher un lot, une cuvée, un
              client…
            </p>
          ) : chargementInitial ? (
            <p className="px-4 py-8 text-center text-[13px] text-champagne-700">Recherche…</p>
          ) : aucunResultat ? (
            <div className="px-4 py-8 text-center">
              <p className="text-[14px] font-medium text-aubergine-900">
                Aucun résultat pour « {requeteDebouncee} »
              </p>
              <p className="mt-1 text-[13px] text-champagne-700">
                Essayez un n° de lot ou un nom de client.
              </p>
            </div>
          ) : (
            groupes.map((groupe, i) => (
              <GroupeResultats
                key={groupe.cle}
                groupe={groupe}
                startIndex={offsets[i] ?? 0}
                activeIndex={indexActif}
                afficherType={groupe.cle === "recents"}
                onSelect={ouvrirResultat}
                onSurvol={setIndexActif}
              />
            ))
          )}
        </div>

        {/* Aide clavier en pied (contrat). */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-champagne-100 px-4 py-2 text-[11px] text-champagne-600">
          <span className="inline-flex items-center gap-1">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> naviguer
          </span>
          <span className="inline-flex items-center gap-1">
            <Kbd>↵</Kbd> ouvrir
          </span>
          <span className="inline-flex items-center gap-1">
            <Kbd>Échap</Kbd> fermer
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Petite touche clavier stylée pour l'aide en pied de palette. */
function Kbd({ children }: { children: string }) {
  return (
    <kbd className="inline-flex min-w-[18px] items-center justify-center rounded border border-champagne-200 bg-champagne-50 px-1 py-0.5 font-sans text-[10px] text-champagne-700">
      {children}
    </kbd>
  );
}
